import { StepLayout } from "#/components/carrot-plans/shared/StepLayout"
import {
  ButtonFooterLayout,
  ContinueButton,
  RoutingFlowTitle,
  SkipButton,
  useConvertTranslatedLabel
} from "#/components/carrot-plans/shared"
import React from "react"
import { Form } from "@carrotfertility/carotene-core-x"
import { defineMessage, FormattedMessage, MessageDescriptor, useIntl } from "react-intl"
import { FormRadioButtonGroup } from "#/services/common-forms"
import { useGetMedicalInfo, useMedicalInfo } from "#/components/carrot-plans/context/MedicalInfoContext"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"

type BabyFeedingFieldName = "pregnancyBabyFeedingPlan" | "postpartumBabyFeedingStatus"

function BabyFeedingStep({
  fieldName,
  headerText,
  options
}: {
  fieldName: BabyFeedingFieldName
  headerText: MessageDescriptor
  options: { label: MessageDescriptor; value: string }[]
}): JSX.Element {
  const intl = useIntl()
  const { updateMedicalInfo, hasQuestionBeenSkipped } = useMedicalInfo()
  const medicalInfoQuery = useGetMedicalInfo()
  const defaultValue = medicalInfoQuery.data?.[fieldName]

  const { send } = useStateMachine(defaultValue || hasQuestionBeenSkipped(fieldName) ? "" : null)

  async function onClickContinue(formValues: Record<string, string>): Promise<void> {
    const value = formValues[fieldName]
    if (value != null) {
      await updateMedicalInfo({ [fieldName]: value })
      send("")
    } else {
      await onSkip()
    }
  }

  async function onSkip(): Promise<void> {
    await updateMedicalInfo({ skipFields: [fieldName] })
    send("")
  }

  const radioOptions = useConvertTranslatedLabel(options)
  const fieldLabelId = `${fieldName}-label`

  return (
    <>
      <RoutingFlowTitle title={intl.formatMessage(headerText)} />
      <StepLayout
        header={<span id={fieldLabelId}>{intl.formatMessage(headerText)}</span>}
        formElements={
          <Form
            defaultValues={{
              [fieldName]: defaultValue || ""
            }}
            onSubmit={onClickContinue}
          >
            <FormRadioButtonGroup name={fieldName} aria-labelledby={fieldLabelId} options={radioOptions} />
            <ButtonFooterLayout
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
              skipButton={<SkipButton onClick={onSkip} />}
            />
          </Form>
        }
      />
    </>
  )
}

const pregnantBabyFeedingHeader = defineMessage({ defaultMessage: "What do you plan to feed your baby?" })

const pregnantBabyFeedingOptions = [
  { label: defineMessage({ defaultMessage: "Breastmilk only" }), value: "BREASTFEEDING" },
  { label: defineMessage({ defaultMessage: "Formula only" }), value: "FORMULA" },
  { label: defineMessage({ defaultMessage: "Breastmilk and formula" }), value: "MIXED_FEEDING" },
  { label: defineMessage({ defaultMessage: "I'm unsure/haven't decided yet" }), value: "NOT_SURE" }
]

export const PregnantBabyFeedingStep = (): JSX.Element => (
  <BabyFeedingStep
    fieldName="pregnancyBabyFeedingPlan"
    headerText={pregnantBabyFeedingHeader}
    options={pregnantBabyFeedingOptions}
  />
)

const postpartumBabyFeedingHeader = defineMessage({
  defaultMessage: "If you are parenting an infant, what are you currently feeding your baby?"
})

const postpartumBabyFeedingOptions = [
  { label: defineMessage({ defaultMessage: "Breastmilk only" }), value: "BREASTFEEDING" },
  { label: defineMessage({ defaultMessage: "Formula only" }), value: "FORMULA" },
  { label: defineMessage({ defaultMessage: "Breastmilk and formula" }), value: "MIXED_FEEDING" }
]

export const PostpartumBabyFeedingStep = (): JSX.Element => (
  <BabyFeedingStep
    fieldName="postpartumBabyFeedingStatus"
    headerText={postpartumBabyFeedingHeader}
    options={postpartumBabyFeedingOptions}
  />
)
